import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, Copy, Plus, Pencil, Eye, EyeOff, Wallet } from "lucide-react";
import { toast } from "sonner";
import { statusBadge, inputCls, outlineBtn } from "./shared";

const AffiliateDetailDialog = ({
  affDetail, setAffDetail, affLinks, affNotes,
  newLink, setNewLink, destinations, editingLink, saveLink, toggleLink, startEditLink, cancelEditLink,
  newNote, setNewNote, addNote,
  paySel, togglePaySel, unpaidRefs, payTotal, payMeta, setPayMeta, recordPayment,
  copyText, revealPayment,
}) => {
  const aff = affDetail.affiliate;
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => { setRevealed(null); }, [aff?.id, affDetail.open]);

  const doReveal = async () => {
    setRevealing(true);
    try { setRevealed(await revealPayment(aff.id)); }
    catch { toast.error("Could not reveal payment details"); }
    finally { setRevealing(false); }
  };

  return (
  <Dialog open={affDetail.open} onOpenChange={(o) => setAffDetail({ ...affDetail, open: o })}>
    <DialogContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)] rounded-none max-w-3xl" data-testid="affiliate-detail-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl flex items-center gap-2"><Handshake className="w-5 h-5 text-[var(--ir-orange)]" />{aff?.name}</DialogTitle>
        <DialogDescription className="text-[var(--ir-muted)]">
          {aff && (
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>Clicks: <strong className="text-[var(--ir-text)]">{aff.clicks}</strong></span>
              <span>Sign-ups: <strong className="text-[var(--ir-text)]">{aff.signups}</strong></span>
              <span>Approved: <strong className="text-[var(--ir-text)]">{aff.approved_referrals}</strong></span>
              <span>Owed: <strong className="text-[var(--ir-text)]">${aff.commission_owed.toFixed(2)}</strong></span>
              <span>Paid: <strong className="text-[var(--ir-text)]">${aff.commission_paid.toFixed(2)}</strong></span>
            </span>
          )}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6 pt-1 max-h-[65vh] overflow-y-auto pr-1">
        {/* PAYMENT DETAILS (masked + audited reveal) */}
        <div className="border border-[var(--ir-border)] rounded p-4" data-testid="payment-details-section">
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-1 flex items-center gap-2"><Wallet className="w-4 h-4 text-[var(--ir-orange)]" />Payment details</h4>
          {aff && !aff.has_payment_info ? (
            <p className="text-[var(--ir-muted)] text-sm">The affiliate hasn't added any payout details yet.</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-[var(--ir-muted)] text-xs mb-1 block">PayPal email</Label>
                  <div className="font-mono text-sm text-[var(--ir-text)]" data-testid="payment-paypal-value">{revealed ? (revealed.paypal_email || "—") : (aff?.paypal_email_masked || "—")}</div>
                </div>
                <div>
                  <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Mailing address</Label>
                  <div className="text-sm text-[var(--ir-text)] whitespace-pre-wrap" data-testid="payment-address-value">{revealed ? (revealed.mailing_address || "—") : (aff?.mailing_address_masked || "—")}</div>
                </div>
              </div>
              {revealed ? (
                <Button onClick={() => setRevealed(null)} variant="outline" className={`${outlineBtn} mt-3`} data-testid="hide-payment-btn"><EyeOff className="w-3.5 h-3.5 mr-1" />Hide</Button>
              ) : (
                <Button onClick={doReveal} disabled={revealing} className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none h-9 mt-3 disabled:opacity-50" data-testid="reveal-payment-btn"><Eye className="w-3.5 h-3.5 mr-1" />{revealing ? "Revealing…" : "Reveal"}</Button>
              )}
              <p className="text-[var(--ir-muted)] text-[11px] mt-2">Revealing full payment details is logged for audit.</p>
            </>
          )}
        </div>

        {/* REFERRALS with per-referral rate */}
        <div>
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-2">Referred members ({affDetail.referrals.length})</h4>
          <div className="border border-[var(--ir-border)] rounded">
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
                  <TableHead className="text-[var(--ir-muted)]">Name</TableHead>
                  <TableHead className="text-[var(--ir-muted)]">Status</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Rate</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-right">Commission</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Paid</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {affDetail.referrals.length === 0 && (
                  <TableRow className="border-[var(--ir-border)]"><TableCell colSpan={5} className="text-center text-[var(--ir-muted)] py-6">No referrals yet.</TableCell></TableRow>
                )}
                {affDetail.referrals.map((r) => (
                  <TableRow key={r.id} className="border-[var(--ir-border)]" data-testid={`ref-row-${r.id}`}>
                    <TableCell className="text-[var(--ir-text)]">
                      <div className="flex items-center gap-2">{r.name}{r.is_affiliate && <Badge className="rounded-none border bg-[var(--ir-orange)]/10 text-[var(--ir-orange)] border-[var(--ir-orange)]/30 text-[10px]">Affiliate</Badge>}</div>
                      {r.via_link && <div className="text-[var(--ir-blue)] text-xs">via {r.via_link}</div>}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-center text-[var(--ir-text)]" data-testid={`ref-rate-${r.id}`}>{r.commission_rate != null ? `${r.commission_rate}%` : "—"}</TableCell>
                    <TableCell className="text-right text-[var(--ir-text)]">{r.commission_amount != null ? `$${Number(r.commission_amount).toFixed(2)}` : "—"}</TableCell>
                    <TableCell className="text-center">{r.status === "approved" ? (r.commission_paid ? <Badge className="rounded-none border bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Paid</Badge> : <span className="text-[var(--ir-muted)] text-xs">Unpaid</span>) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* RECORD A PAYMENT */}
        <div className="border border-[var(--ir-border)] rounded p-4" data-testid="record-payment-section">
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-3">Record a payment</h4>
          {unpaidRefs.length === 0 ? (
            <p className="text-[var(--ir-muted)] text-sm">No unpaid approved referrals to pay.</p>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {unpaidRefs.map((r) => (
                  <label key={r.id} className="flex items-center gap-3 cursor-pointer text-sm" data-testid={`pay-check-${r.id}`}>
                    <Checkbox checked={paySel.includes(r.id)} onCheckedChange={() => togglePaySel(r.id)} />
                    <span className="text-[var(--ir-text)] flex-1">{r.name}</span>
                    <span className="text-[var(--ir-muted)]">{r.commission_rate}%</span>
                    <span className="text-[var(--ir-text)] font-medium w-16 text-right">${Number(r.commission_amount).toFixed(2)}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input value={payMeta.method} onChange={(e) => setPayMeta({ ...payMeta, method: e.target.value })} placeholder="Method (e.g. PayPal, Check)" className={inputCls} data-testid="pay-method-input" />
                <Input value={payMeta.reference} onChange={(e) => setPayMeta({ ...payMeta, reference: e.target.value })} placeholder="Reference / txn # (optional)" className={inputCls} data-testid="pay-reference-input" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--ir-muted)]">Selected total: <strong className="text-[var(--ir-text)]" data-testid="pay-total">${payTotal.toFixed(2)}</strong></span>
                <Button onClick={recordPayment} disabled={paySel.length === 0} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-none h-9 disabled:opacity-50" data-testid="record-payment-btn">Record payment</Button>
              </div>
            </div>
          )}
        </div>

        {/* CUSTOM REFERRAL LINKS */}
        <div className="border border-[var(--ir-border)] rounded p-4" data-testid="referral-links-section">
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-1">Custom Referral Links ({affLinks.filter((l) => !l.is_default).length})</h4>
          <p className="text-[var(--ir-muted)] text-xs mb-3">Extra trackable links for different promos or landing pages. Attribution works exactly like the default link (same 30-day cookie & permanent attribution). Deactivating preserves historical tracking.</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
                  <TableHead className="text-[var(--ir-muted)]">Label</TableHead>
                  <TableHead className="text-[var(--ir-muted)]">Referral URL</TableHead>
                  <TableHead className="text-[var(--ir-muted)]">Destination</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Comm.</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Clicks</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Conv.</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-center">Status</TableHead>
                  <TableHead className="text-[var(--ir-muted)] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {affLinks.length === 0 && <TableRow className="border-[var(--ir-border)]"><TableCell colSpan={8} className="text-center text-[var(--ir-muted)] py-4">No custom links yet. The affiliate's default link is always active.</TableCell></TableRow>}
                {affLinks.map((l) => (
                  <TableRow key={l.id} className="border-[var(--ir-border)]" data-testid={`link-row-${l.id}`}>
                    <TableCell className="text-[var(--ir-text)] text-sm">{l.name}{l.promo_code && <div className="text-[var(--ir-orange)] text-xs">{l.promo_code}</div>}</TableCell>
                    <TableCell><div className="flex items-center gap-1.5"><code className="text-xs text-[var(--ir-blue)] max-w-[150px] truncate">{l.link}</code><Button size="sm" variant="outline" onClick={() => copyText(l.link, "Link")} className={outlineBtn} title="Copy" data-testid={`copy-link-btn-detail-${l.id}`}><Copy className="w-3 h-3" /></Button></div></TableCell>
                    <TableCell className="text-[var(--ir-text)] text-xs" data-testid={`link-destination-${l.id}`}>{l.destination_label}</TableCell>
                    <TableCell className="text-center text-[var(--ir-text)] text-xs" data-testid={`link-commission-${l.id}`}>{l.commission_percent != null ? `${l.commission_percent}%` : <span className="text-[var(--ir-muted)]">Default</span>}</TableCell>
                    <TableCell className="text-center text-[var(--ir-text)]">{l.clicks}</TableCell>
                    <TableCell className="text-center text-[var(--ir-text)]" data-testid={`link-conversions-${l.id}`}>{l.conversions}</TableCell>
                    <TableCell className="text-center">{l.active ? <Badge className="rounded-none border bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Active</Badge> : <Badge className="rounded-none border bg-[var(--ir-muted)]/10 text-[var(--ir-muted)] border-[var(--ir-muted)]/30 text-[10px]">Inactive</Badge>}</TableCell>
                    <TableCell><div className="flex items-center justify-end gap-1.5">
                      {l.is_default ? <span className="text-[var(--ir-muted)] text-xs">Default</span> : <>
                        <Button size="sm" variant="outline" onClick={() => startEditLink(l)} className={outlineBtn} title="Edit" data-testid={`edit-link-${l.id}`}><Pencil className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => toggleLink(l)} className={outlineBtn} data-testid={`toggle-link-${l.id}`}>{l.active ? "Deactivate" : "Activate"}</Button>
                      </>}
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 border-t border-[var(--ir-border)] pt-4">
            <div className="text-xs font-semibold text-[var(--ir-text)] mb-2">{editingLink ? "Edit link" : "Add link"}</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Label</Label>
                <Input value={newLink.name} onChange={(e) => setNewLink({ ...newLink, name: e.target.value })} placeholder="e.g. Instagram Bio" className={inputCls} data-testid="new-link-name" />
              </div>
              <div>
                <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Destination</Label>
                <Select value={newLink.destination} onValueChange={(v) => setNewLink({ ...newLink, destination: v })}>
                  <SelectTrigger className={`${inputCls} h-10`} data-testid="new-link-destination"><SelectValue placeholder="Select destination" /></SelectTrigger>
                  <SelectContent>
                    {destinations.map((d) => (
                      <SelectItem key={d.key} value={d.key} data-testid={`destination-option-${d.key}`}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Commission % <span className="text-[var(--ir-muted)] font-normal">(optional)</span></Label>
                <Input type="number" min="0" max="100" step="0.01" value={newLink.commission_percent} onChange={(e) => setNewLink({ ...newLink, commission_percent: e.target.value })} placeholder="Blank = affiliate default" className={inputCls} data-testid="new-link-commission" />
              </div>
              <div>
                <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Promo code <span className="text-[var(--ir-muted)] font-normal">(optional)</span></Label>
                <Input value={newLink.promo_code} onChange={(e) => setNewLink({ ...newLink, promo_code: e.target.value })} placeholder="e.g. SAVE20" className={inputCls} data-testid="new-link-promo" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={saveLink} disabled={!newLink.name || !newLink.destination} className="bg-[var(--ir-blue)] hover:opacity-90 text-white rounded-none h-9 disabled:opacity-50" data-testid="add-link-btn"><Plus className="w-4 h-4 mr-1" />{editingLink ? "Save link" : "Add Link"}</Button>
              {editingLink && <Button onClick={cancelEditLink} variant="outline" className={outlineBtn} data-testid="cancel-edit-link-btn">Cancel</Button>}
            </div>
          </div>
        </div>

        {/* ADMIN NOTES */}
        <div className="border border-[var(--ir-border)] rounded p-4" data-testid="admin-notes-section">
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-1">Internal notes <span className="text-[var(--ir-muted)] font-normal">(admin-only)</span></h4>
          <div className="flex gap-2 my-3">
            <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note…" className={inputCls} data-testid="new-note-input" onKeyDown={(e) => { if (e.key === "Enter" && newNote.trim()) addNote(); }} />
            <Button onClick={addNote} disabled={!newNote.trim()} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none h-10 disabled:opacity-50" data-testid="add-note-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2 max-h-44 overflow-y-auto">
            {affNotes.length === 0 && <p className="text-[var(--ir-muted)] text-sm">No notes yet.</p>}
            {affNotes.map((n) => (
              <div key={n.id} className="border-b border-[var(--ir-border)] pb-2" data-testid={`note-${n.id}`}>
                <p className="text-[var(--ir-text)] text-sm">{n.text}</p>
                <p className="text-[var(--ir-muted)] text-xs mt-0.5">{n.admin_name || n.admin_email} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
              </div>
            ))}
          </div>
        </div>

        {/* PAYMENT HISTORY */}
        <div>
          <h4 className="text-sm font-semibold text-[var(--ir-text)] mb-2 flex items-center justify-between">
            <span>Payment history</span>
            {affDetail.affiliate && <span className="text-xs font-normal text-[var(--ir-muted)]">Paid <strong className="text-emerald-600">${affDetail.affiliate.commission_paid.toFixed(2)}</strong> · Outstanding <strong className="text-[var(--ir-text)]">${affDetail.affiliate.commission_owed.toFixed(2)}</strong></span>}
          </h4>
          {affDetail.payments.length === 0 ? (
            <p className="text-[var(--ir-muted)] text-sm">No payments recorded yet.</p>
          ) : (
            <div className="border border-[var(--ir-border)] rounded">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
                    <TableHead className="text-[var(--ir-muted)]">Date</TableHead>
                    <TableHead className="text-[var(--ir-muted)] text-right">Amount</TableHead>
                    <TableHead className="text-[var(--ir-muted)]">Method</TableHead>
                    <TableHead className="text-[var(--ir-muted)]">Reference</TableHead>
                    <TableHead className="text-[var(--ir-muted)] text-center">Referrals</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affDetail.payments.map((p) => (
                    <TableRow key={p.id} className="border-[var(--ir-border)]" data-testid={`payment-row-${p.id}`}>
                      <TableCell className="text-[var(--ir-muted)] text-sm">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : ""}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">${Number(p.amount).toFixed(2)}</TableCell>
                      <TableCell className="text-[var(--ir-text)] text-sm">{p.method || "—"}</TableCell>
                      <TableCell className="text-[var(--ir-text)] text-sm">{p.reference || "—"}</TableCell>
                      <TableCell className="text-center text-[var(--ir-muted)] text-sm" title={(p.referral_names || []).join(", ")}>{p.referral_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </DialogContent>
  </Dialog>
  );
};

export default AffiliateDetailDialog;
